import { FormikValues } from 'formik';
import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import * as Yup from 'yup';
import { Snack } from '../pages/Components/Types';
import { toast } from 'react-toastify';
import { addDaysToInputDate, formatarData } from '../shared/utils/helpers/date';
import OrdemServicoController from '../controllers/OrdemServicoController';
import ClienteController from '../controllers/ClienteController';
import { OrdemServico } from '../routes/RoutesNames';
import { ClienteRequest } from '../controllers/Types';

// Regra: a data de entrega e projetada automaticamente 15 dias apos a data de emissao.
export const PRAZO_ENTREGA_PADRAO_DIAS = 15;

const steps = [
  'Cadastro',
  'Itens do Cliente',
  'Peças',
  'Serviços',
  'Finalizar',
];

// O status da OS define o grau de edicao da tela:
//   Aberto / Em Manutencao / Aguardando Aprovacao -> todos os campos liberados
//   A Faturar (gravado como "Faturamento") / Reprovado / Finalizado -> apenas
//   Data de Entrega e Descricao; os demais campos ficam travados.
// "executar" e "orcamento" entram na lista porque sao os valores que o menu de
// status da pesquisa envia para virar Em Manutencao / Aguardando Aprovacao.
// Qualquer status fora da lista cai no modo restrito (o lado seguro).
const STATUS_EDICAO_LIVRE = [
  'aberto',
  'emmanutenção',
  'aguardandoaprovação',
  'executar',
  'orçamento',
];

// replace(/\s/g) em vez de replaceAll: o lib do projeto ainda nao tem replaceAll.
const normalizarStatus = (status?: string | null): string =>
  (status || '').toLowerCase().replace(/\s/g, '');

interface Usuario {
  usu_id: number;
  usu_nome: string;
}

interface Cliente {
  cli_id: number;
  cli_nome: string;
}

export const useOrderServicoCadastrar = (id: number | null, isCloning = false, duploClick = false) => {
  // @ts-ignore - .
  const userId = useSelector((state) => state.Authentication);
  const OS_Id = id;
  const [single, setSingle] = useState<any | null>(null);
  const [activeStep, setActiveStep] = useState(0);
  const [snack, setSnack] = useState<Snack | null>(null);
  const [modalItensOpen, setModalItensOpen] = useState(false);
  const [modalPecasOpen, setModalPecasOpen] = useState(false);
  const [modalServicosOpen, setModalServicosOpen] = useState(false);
  const [usuarios, setUsuarios] = useState<Usuario[]>([]);
  const [usuarioSelecionado, setUsuarioSelecionado] = useState<Cliente | null>(null);
  const [clientes, setClientes] = useState<Cliente[]>([]);
  const [clienteSelecionado, setClienteSelecionado] = useState<Cliente | null>(null);
  const [snackOpen, setSnackOpen] = useState(false);
  const navigation = useNavigate();
  const [loading, setLoading] = useState(false);
  const [isTryingToAdvance, setIsTryingToAdvance] = useState(false);
  const [initialValues, setInitialValues] = useState<FormikValues>({
    UsuId: userId || '',
    os_id: OS_Id || null,
    os_data_emissao: formatarData(new Date()),
    os_dataEntrega: addDaysToInputDate(formatarData(new Date()), PRAZO_ENTREGA_PADRAO_DIAS),
    os_responsavelId: null,
    os_cliId: null,
    os_descricao: '',
    itensJson: [],
    pecasjson: [],
    servicosjson: [],
    os_status: 'Aberto',
    os_gerarPedido: true,
  });

  useEffect(() => {
    if (OS_Id !== null && OS_Id !== undefined) {
      getSingle(OS_Id);
    }
  }, [OS_Id]);

  const getSingle = async (OS_Id: number) => {
    setLoading(true);
    const Response = await OrdemServicoController.ListarUnico(OS_Id);
    setLoading(false);
    setSingle(Response.Data);
  };

  useEffect(() => {
    const fetchUsuario = async () => {
      if (single) {
        try {
          const responseUsuario = await ClienteController.ListarVendedor(single.Responsavel);
          setUsuarios(responseUsuario.Data);

          const responseCliente = await ClienteController.Listar({ Nome: '', Celular: '', Endereco: '', Bairro: '', Cidade: '', ClienteId: single.ClienteId, ClienteAtivo: 'A' });

          if (responseCliente.StatusCode === 200 && responseCliente?.Data?.length > 0)
            setClientes(responseCliente.Data);

          setInitialValues({
            UsuId: userId || '',
            os_id: isCloning ? '' : (single.OS_id || ''),
            os_data_emissao: formatarData(new Date(single.DataEmissao || Date.now())),
            os_dataEntrega: formatarData(new Date(single.DataEntrega || Date.now())),
            os_responsavelId: single.ResponsavelId || null,
            os_cliId: single.ClienteId || null,
            os_descricao: single.Descricao || '',
            itensJson: single.itensJson || [],
            pecasjson: single.pecasjson || [],
            servicosjson: single.servicosjson || [],
            os_status: isCloning ? 'Aberto' : (single?.os_status || 'Aberto'),
            os_gerarPedido: isCloning ? true : single?.OS_gerarPedido?.toLowerCase() === 's',
          });
        } catch (error) {
          console.error('Erro ao buscar Ordem de Serviço:', error);
        }
      }
    };

    fetchUsuario();
  }, [single, userId]);

  const validationSchema = Yup.object().shape({
    os_data_emissao: Yup.string()
      .required('Campo Obrigatório!')
      .matches(/^\d{4}-\d{2}-\d{2}$/, 'Data inválida! Use o formato AAAA-MM-DD'),
    os_dataEntrega: Yup.string()
      .required('Campo Obrigatório!')
      .matches(/^\d{4}-\d{2}-\d{2}$/, 'Data inválida! Use o formato AAAA-MM-DD')
      .test('is-later', 'A data de entrega deve ser posterior à data de emissão', function (value) {
        const { os_data_emissao } = this.parent;
        return value && os_data_emissao && new Date(value) >= new Date(os_data_emissao);
      }),
    os_responsavelId: Yup.string()
      .required('Campo Obrigatório!'),
    os_cliId: Yup.string()
      .required('Campo Obrigatório!'),
    itensJson: Yup.array().of(Yup.object()).min(1, 'Deve haver pelo menos um item!'),
    //pecasjson: Yup.array().of(Yup.object()).min(1, 'Deve haver pelo menos uma peça!'),
    servicosjson: Yup.array().of(Yup.object()).min(1, 'Deve haver pelo menos um serviço!'),
  });

  const handleNext = async (validateForm: any, setErrors: any, toStep: number | null = null) => {
    setIsTryingToAdvance(true);
    const errors = await validateForm();
    if (Object.keys(errors).length === 0 || (toStep !== null && activeStep >= toStep)) {
      if (toStep !== null) {
        setActiveStep(toStep)
      } else {
        setActiveStep((prevStep) => prevStep + 1);
      }
    } else {
      const filteredErrors = Object.keys(errors).filter((key: string) => key !== 'itensJson' && key !== 'pecasjson' && key !== 'servicosjson');

      if (activeStep === 0 && filteredErrors.length === 0) {
        setActiveStep((prevStep) => prevStep + 1);
      }
      else if (activeStep === 1) {
        if (errors.itensJson) {
          setSnackOpen(true);
        } else {
          setActiveStep((prevStep) => prevStep + 1);
        }
      }
      else if (activeStep === 2) {
        // Esse step não tem validação específica, então apenas avançamos
        setActiveStep((prevStep) => prevStep + 1);
      }
      else if (activeStep === 3) {
        if (errors.servicosjson) {
          setSnackOpen(true);
        } else {
          setActiveStep((prevStep) => prevStep + 1);
        }
      }
      else {
        setErrors(errors);
      }
    }
  };

  const handleBack = () => {
    if (activeStep === 0) {
      history.back();
    } else {
      setActiveStep((prevStep) => prevStep - 1);
    }
  };

  const handleCloseSnack = () => {
    setSnackOpen(false);
  };

  const handleOpenItensModal = () => setModalItensOpen(true);
  const handleCloseItensModal = () => setModalItensOpen(false);

  const handleOpenPecasModal = () => setModalPecasOpen(true);
  const handleClosePecasModal = () => setModalPecasOpen(false);

  const handleOpenServicosModal = () => setModalServicosOpen(true);
  const handleCloseServicosModal = () => setModalServicosOpen(false);

  const handleSubmit = async (values: any, { setSubmitting, resetForm }: any) => {
    let response;

    if (!isCloning && values.os_id > 0) { //
      console.log(values)
      response = await OrdemServicoController.Atualizar(values);
    } else {
      console.log('Adicionando nova OS com os valores: ', values);
      response = await OrdemServicoController.Adicionar(values);
    }

    if (response.StatusCode === 204) {
      toast('Ordem de Serviço salva com sucesso!', {
                type: 'success',
              });
      resetForm();
      navigation(OrdemServico);
    } else {
      toast('Erro ao salvar ordem de serviço!', {
                type: 'error',
              });
    }

    setSubmitting(false);
  };

  const CloseModal = (): void => {
    setSnack(null);
  };

  // true = OS em status que so aceita Data de Entrega e Descricao.
  // Criacao e clonagem nunca sao restritas (a OS nasce em Aberto).
  const status = normalizarStatus(single?.os_status);
  const edicaoRestrita =
    !isCloning &&
    Boolean(status) &&
    !STATUS_EDICAO_LIVRE.some((livre) => status.includes(livre));

  /* [28/09] A flag "Gerar pedido ao finalizar" tem regra PROPRIA, mais larga que a da
     O.S. inteira.

     Ate agora ela seguia o edicaoRestrita, e o status FATURAMENTO ("A Faturar") nao esta
     em STATUS_EDICAO_LIVRE - entao a O.S. inteira ficava travada e a flag junto. So que
     a finalizacao em lote EXIGE a flag ligada, e recusa o lote nomeando a O.S. que estiver
     com ela desmarcada: sem poder liga-la em A Faturar, a recusa viraria beco sem saida.

     Os tres status sao os mesmos que a finalizacao em lote aceita. Nenhum outro campo
     muda de comportamento - o edicaoRestrita continua valendo para o resto da tela. */
  const podeEditarGerarPedido =
    isCloning ||
    !status ||
    !edicaoRestrita ||
    ['emmanutenção', 'aguardandoaprovação', 'faturamento'].some((s) => status.includes(s));

  useEffect(() => {
    const fetchUsuarios = async () => {
      if (usuarioSelecionado?.cli_nome && usuarioSelecionado?.cli_nome.length >= 1) {
        const response = await ClienteController.ListarVendedor(usuarioSelecionado?.cli_nome);
        if (response.StatusCode === 200) {
          setUsuarios(response.Data);
        }
      } else {
        setUsuarios([]);
      }
    };

    fetchUsuarios();
  }, [usuarioSelecionado]);

  useEffect(() => {
    const nome = clienteSelecionado?.cli_nome;
    if (!nome || nome.length < 1) {
      setClientes([]);
      return;
    }

    // Debounce + guarda "latest wins": evita disparo a cada tecla e descarta
    // respostas antigas que chegam fora de ordem (corrida que trazia nomes sem relacao).
    let active = true;
    const timer = setTimeout(async () => {
      const request: ClienteRequest = {
        ClienteId: clienteSelecionado.cli_id || null,
        Nome: nome,
        Celular: '',
        Endereco: '',
        Bairro: '',
        Cidade: '',
        DataNascimento: null,
        Transportadora: '',
        ClienteAtivo: 'A',
      };
      const response = await ClienteController.Listar(request);
      if (active && response.StatusCode === 200) {
        const lista = Array.isArray(response.Data) ? response.Data : [];
        lista.sort((a: any, b: any) =>
          (a?.cli_nome || '').localeCompare(b?.cli_nome || '')
        );
        setClientes(lista);
      }
    }, 400);

    return () => {
      active = false;
      clearTimeout(timer);
    };
  }, [clienteSelecionado]);

  return {
    duploClick,
    steps,
    activeStep,
    snack,
    modalItensOpen,
    modalPecasOpen,
    modalServicosOpen,
    usuarios,
    setUsuarioSelecionado,
    clientes,
    setClienteSelecionado,
    snackOpen,
    loading,
    isTryingToAdvance,
    initialValues,
    validationSchema,
    handleNext,
    handleBack,
    handleCloseSnack,
    handleOpenItensModal,
    handleCloseItensModal,
    handleOpenPecasModal,
    handleClosePecasModal,
    handleOpenServicosModal,
    handleCloseServicosModal,
    handleSubmit,
    CloseModal,
    edicaoRestrita,
    podeEditarGerarPedido,
  };
};
